// Updated TicketList.jsx with strict access control and red blink notifications

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  User,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ExternalLink,
  TrendingUp,
  Users,
  MessageCircle,
  X
} from 'lucide-react';

// FIXED: Import lowercase ticketService (singleton instance)
import ticketService from '../../services/ticketService';
import ChatRoom from '../chat/ChatRoom';
import { useAuth } from '../../contexts/AuthContext';

const TicketList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    user, 
    isAgent, 
    isAdmin, 
    isCustomerCare,
    hasPermission, 
    hasAnyPermission,
    getDebugInfo 
  } = useAuth();
  
  // State declarations
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    priority: '',
    page: 1
  });
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState(null);
  
  // STRICT: Store API permissions and access level
  const [apiPermissions, setApiPermissions] = useState(null);
  const [userAccessLevel, setUserAccessLevel] = useState('own_only');
  
  // Chat-related state
  const [showChat, setShowChat] = useState(false);
  const [selectedTicketForChat, setSelectedTicketForChat] = useState(null);

  // NEW: Track tickets with new messages for red blink effect
  const [ticketsWithNewMessages, setTicketsWithNewMessages] = useState(new Set());
  const [messageNotifications, setMessageNotifications] = useState(new Map());

  // STRICT: Permission checking based on actual access level
  const hasApiPermission = useCallback((permission) => {
    if (!apiPermissions) return false;
    
    const permissionKey = permission.replace('tickets.', '').replace('-', '_');
    
    console.log('STRICT API Permission Check:', {
      permission,
      permissionKey,
      apiPermissions,
      userAccessLevel,
      result: apiPermissions[permissionKey] === true
    });
    
    return apiPermissions[permissionKey] === true;
  }, [apiPermissions, userAccessLevel]);

  // STRICT: Determine what user can do based on access level
  const canCreateTicket = hasApiPermission('tickets.create');
  const canCreateOnBehalf = hasApiPermission('tickets.create-behalf') && (isAgent() || isAdmin() || isCustomerCare());
  const canViewAllTickets = userAccessLevel === 'all';
  const canViewOnlyAssigned = userAccessLevel === 'assigned_only';
  const canViewOnlyOwn = userAccessLevel === 'own_only';

  // Business logic based on STRICT access levels
  const isCustomerUser = userAccessLevel === 'own_only';
  const isStaffUser = userAccessLevel === 'assigned_only';
  const isAdminUser = userAccessLevel === 'all';

  const shouldShowPersonalCreate = isCustomerUser && canCreateTicket;
  const shouldShowOnBehalfCreate = canCreateOnBehalf;
  
  // STRICT: Access check - only allow if user has proper access level
  const canAccessTickets = userAccessLevel !== 'none' && apiPermissions !== null;

  // NEW: Handle new message notifications
  const handleNewMessage = useCallback((message, ticketId) => {
    console.log('New message received for ticket:', ticketId, message);
    
    // Add ticket to new messages set
    setTicketsWithNewMessages(prev => new Set([...prev, ticketId]));
    
    // Store message notification with timestamp
    setMessageNotifications(prev => new Map([
      ...prev,
      [ticketId, {
        message: message.message || 'New message',
        senderName: message.user?.name || 'User',
        timestamp: Date.now()
      }]
    ]));

    // Auto-remove the blink effect after 10 seconds
    setTimeout(() => {
      setTicketsWithNewMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }, 10000);
  }, []);

  // NEW: Clear notification when user interacts with ticket
  const clearTicketNotification = useCallback((ticketId) => {
    setTicketsWithNewMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(ticketId);
      return newSet;
    });
    
    setMessageNotifications(prev => {
      const newMap = new Map(prev);
      newMap.delete(ticketId);
      return newMap;
    });
  }, []);

  // Enhanced debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 STRICT ACCESS CONTROL DEBUG:', {
        user: user,
        apiPermissions: apiPermissions,
        userAccessLevel: userAccessLevel,
        
        // Access level checks
        canViewAllTickets,
        canViewOnlyAssigned,
        canViewOnlyOwn,
        
        // User type classifications
        isCustomerUser,
        isStaffUser,
        isAdminUser,
        
        // Action permissions
        shouldShowPersonalCreate,
        shouldShowOnBehalfCreate,
        
        // Final access decision
        canAccessTickets,
        
        // Context checks
        isAgent: isAgent(),
        isAdmin: isAdmin(),
        isCustomerCare: isCustomerCare(),
      });
    }
  }, [user, apiPermissions, userAccessLevel, canViewAllTickets, canViewOnlyAssigned, canViewOnlyOwn, canAccessTickets]);

  // STRICT: Fetch tickets and extract access level from API response
  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('STRICT: Fetching tickets with filters:', filters);
      
      // FIXED: Use lowercase ticketService
      const response = await ticketService.getTickets(filters);
      console.log('STRICT: API Response:', response);
      
      // CRITICAL: Extract permissions and determine access level from API response
      if (response?.data?.user_permissions) {
        console.log('STRICT: Setting API permissions from response:', response.data.user_permissions);
        setApiPermissions(response.data.user_permissions);
        
        // Determine strict access level
        const permissions = response.data.user_permissions;
        let accessLevel = 'none';
        
        if (permissions.is_admin || permissions.view_all) {
          accessLevel = 'all';
        } else if (permissions.is_agent) {
          accessLevel = 'assigned_only';
        } else if (permissions.create || permissions.view_own || permissions.comment) {
          accessLevel = 'own_only';
        }
        
        console.log('STRICT: Determined access level:', accessLevel);
        setUserAccessLevel(accessLevel);
      }
      
      // Handle ticket data
      if (response?.data) {
        setTickets(response.data.tickets || []);
        setPagination(response.data.pagination || null);
        
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      } else if (Array.isArray(response)) {
        setTickets(response);
        setPagination(null);
      } else if (response?.tickets) {
        setTickets(response.tickets);
        setPagination(response.pagination || null);
      } else {
        setTickets(response || []);
        setPagination(null);
      }
    } catch (err) {
      console.error('STRICT: Failed to fetch tickets:', err);
      setError(err.message || 'Failed to load tickets');
      setTickets([]);
      setPagination(null);
      setApiPermissions(null);
      setUserAccessLevel('none');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Other callbacks remain the same...
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  }, []);

  const handleSearchChange = useCallback((value) => {
    clearTimeout(handleSearchChange.timeoutId);
    handleSearchChange.timeoutId = setTimeout(() => {
      handleFilterChange('search', value);
    }, 300);
  }, [handleFilterChange]);

  const navigateToTicket = useCallback((ticketId) => {
    // Clear notification when navigating to ticket
    clearTicketNotification(ticketId);
    navigate(`/tickets/${ticketId}`);
  }, [navigate, clearTicketNotification]);

  const navigateToCreate = useCallback(() => {
    navigate('/tickets/new');
  }, [navigate]);

  const navigateToCreateOnBehalf = useCallback(() => {
    navigate('/tickets/new?mode=on-behalf');
  }, [navigate]);

  const handleOpenChat = useCallback((ticket) => {
    console.log('Opening chat for ticket:', ticket);
    // Clear notification when opening chat
    clearTicketNotification(ticket.id);
    setSelectedTicketForChat(ticket);
    setShowChat(true);
  }, [clearTicketNotification]);

  const handleCloseChat = useCallback(() => {
    setShowChat(false);
    setSelectedTicketForChat(null);
  }, []);

  const calculateLocalStats = useCallback(() => {
    if (stats) return stats;
    
    return {
      active: tickets.filter(t => ['open', 'assigned', 'in_progress', 'pending_customer'].includes(t.status)).length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      high_priority: tickets.filter(t => ['high', 'critical'].includes(t.priority)).length,
      total: tickets.length,
      overdue: tickets.filter(t => {
        if (typeof ticketService.isOverdue === 'function') {
          return ticketService.isOverdue(t);
        }
        return false;
      }).length
    };
  }, [tickets, stats]);

  // Effects
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // STRICT: Show loading while permissions are being determined
  if (isLoading && userAccessLevel === 'own_only' && !apiPermissions) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading permissions and tickets...</p>
          </div>
        </div>
      </div>
    );
  }

  // STRICT: Access denied check
  if (userAccessLevel === 'none' || !canAccessTickets) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Access Denied</p>
              <p className="text-sm mt-1">You don't have permission to view tickets. Please contact your administrator.</p>
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-2 text-xs">
                  <p>Debug info:</p>
                  <p>User Access Level: {userAccessLevel}</p>
                  <p>API Permissions: {JSON.stringify(apiPermissions || {})}</p>
                  <p>Can Access Tickets: {canAccessTickets ? 'Yes' : 'No'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STRICT: Get page title and description based on access level
  const getPageTitle = () => {
    switch (userAccessLevel) {
      case 'all':
        return 'All Support Tickets';
      case 'assigned_only':
        return 'My Assigned Tickets';
      case 'own_only':
        return 'My Support Requests';
      default:
        return 'Support Tickets';
    }
  };

  const getPageDescription = () => {
    switch (userAccessLevel) {
      case 'all':
        return 'Manage and track all support tickets across the system';
      case 'assigned_only':
        return 'Manage tickets assigned to you';
      case 'own_only':
        return 'Track and manage your support requests';
      default:
        return 'Support ticket management';
    }
  };

  // Rest of the utility functions with FIXED references
  const handleSearch = (e) => {
    const value = e.target.value;
    handleSearchChange(value);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'assigned': return <User className="h-4 w-4 text-blue-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'pending_customer': return <ExternalLink className="h-4 w-4 text-purple-500" />;
      case 'escalated': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'resolved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'closed': return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    // FIXED: Use lowercase ticketService
    const statusConfig = ticketService.formatStatus(status);
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfig.bgColor} ${statusConfig.textColor} border-${statusConfig.color}-200`}>
        {getStatusIcon(status)}
        {statusConfig.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    // FIXED: Use lowercase ticketService
    const priorityConfig = ticketService.formatPriority(priority);
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityConfig.bgColor} ${priorityConfig.textColor} border-${priorityConfig.color}-200`}>
        {priorityConfig.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      // FIXED: Use lowercase ticketService
      if (typeof ticketService.calculateTimeElapsed === 'function') {
        return ticketService.calculateTimeElapsed(dateString);
      }
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  const ticketNeedsAttention = (ticket) => {
    // FIXED: Use lowercase ticketService
    if (typeof ticketService.needsAttention === 'function') {
      return ticketService.needsAttention(ticket);
    }
    return ['high', 'critical'].includes(ticket.priority) && ['open', 'assigned'].includes(ticket.status);
  };

  const getSLAIndicator = (ticket) => {
    // FIXED: Use lowercase ticketService
    if (typeof ticketService.getSLAStatus === 'function') {
      const slaStatus = ticketService.getSLAStatus(ticket);
      if (!slaStatus || slaStatus === 'normal') return null;

      const indicators = {
        overdue: { color: 'text-red-600', bg: 'bg-red-50', label: 'Overdue' },
        critical: { color: 'text-orange-600', bg: 'bg-orange-50', label: 'Due Soon' },
        warning: { color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Due Today' }
      };

      const indicator = indicators[slaStatus];
      if (!indicator) return null;

      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${indicator.bg} ${indicator.color}`}>
          <Clock className="h-3 w-3 mr-1" />
          {indicator.label}
        </span>
      );
    }
    return null;
  };

  const isChatAvailable = (ticket) => {
    // FIXED: Use lowercase ticketService
    if (typeof ticketService.canUserAccessChat === 'function' && 
        typeof ticketService.isChatEnabled === 'function') {
      return ticketService.canUserAccessChat(user, ticket) && ticketService.isChatEnabled(ticket);
    }
    return ticket.agent_id && ['assigned', 'in_progress'].includes(ticket.status);
  };

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Failed to load tickets</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
          <div className="mt-3">
            <button 
              onClick={fetchTickets}
              className="inline-flex items-center gap-2 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded border border-red-300 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const localStats = calculateLocalStats();

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Chat Modal */}
      {showChat && selectedTicketForChat && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-5/6 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                Chat - Ticket #{selectedTicketForChat.id}: {selectedTicketForChat.title}
              </h3>
              <button
                onClick={handleCloseChat}
                className="text-gray-400 hover:text-gray-600 p-1 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ChatRoom
                ticketId={selectedTicketForChat.id}
                user={user}
                onClose={handleCloseChat}
                onNewMessage={(message) => handleNewMessage(message, selectedTicketForChat.id)}
              />
            </div>
          </div>
        </div>
      )}

      {/* STRICT: Header with access-level-aware titles */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {getPageTitle()}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {getPageDescription()}
          </p>
          {process.env.NODE_ENV === 'development' && (
            <p className="mt-1 text-xs text-blue-600">
              Debug: Access Level = {userAccessLevel}
            </p>
          )}
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          {shouldShowOnBehalfCreate && (
            <button
              onClick={navigateToCreateOnBehalf}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Users className="h-4 w-4 mr-2" />
              Create on Behalf
            </button>
          )}
          {shouldShowPersonalCreate && (
            <button
              onClick={navigateToCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Ticket
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by ticket ID, title, or description..."
                defaultValue={filters.search}
                onChange={handleSearch}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none transition-colors"
            >
              <option value="">All Status</option>
              {/* FIXED: Use lowercase ticketService */}
              {ticketService.getStatusOptions().map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority Filter */}
          <div className="relative">
            <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none transition-colors"
            >
              <option value="">All Priority</option>
              {/* FIXED: Use lowercase ticketService */}
              {ticketService.getPriorityLevels().map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats - Updated labels based on access level */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-semibold text-blue-600">
              {localStats.active}
            </p>
            <p className="text-xs text-gray-500">
              {userAccessLevel === 'assigned_only' ? 'Active Assigned' : 'Active'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-green-600">
              {localStats.resolved}
            </p>
            <p className="text-xs text-gray-500">
              {userAccessLevel === 'assigned_only' ? 'Resolved by Me' : 'Resolved'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-orange-600">
              {localStats.high_priority}
            </p>
            <p className="text-xs text-gray-500">High Priority</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-red-600">
              {localStats.overdue || 0}
            </p>
            <p className="text-xs text-gray-500">
              {userAccessLevel === 'assigned_only' ? 'My Overdue' : 'Overdue'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">
              {localStats.total}
            </p>
            <p className="text-xs text-gray-500">
              {userAccessLevel === 'assigned_only' ? 'My Total' : 'Total'}
            </p>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto" />
              <p className="mt-2 text-sm text-gray-500">Loading tickets...</p>
            </div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.status || filters.priority
                ? "Try adjusting your filters"
                : userAccessLevel === 'assigned_only'
                ? "No tickets are currently assigned to you"
                : userAccessLevel === 'own_only'
                ? shouldShowPersonalCreate
                  ? "Get started by creating your first support ticket"
                  : "You have no support tickets"
                : "No tickets to display"
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {tickets.map((ticket) => {
              const needsAttention = ticketNeedsAttention(ticket);
              const slaIndicator = getSLAIndicator(ticket);
              // FIXED: Use lowercase ticketService
              const resolutionTime = typeof ticketService.calculateResolutionTime === 'function' 
                ? ticketService.calculateResolutionTime(ticket) 
                : null;
              const chatAvailable = isChatAvailable(ticket);
              const hasNewMessage = ticketsWithNewMessages.has(ticket.id);
              const messageNotification = messageNotifications.get(ticket.id);
              
              return (
                <div
                  key={ticket.id}
                  className={`p-4 hover:bg-gray-50 transition-colors relative ${
                    needsAttention ? 'bg-red-50 border-l-4 border-l-red-500' : ''
                  } ${hasNewMessage ? 'bg-blue-50' : ''}`}
                >
                  {/* NEW: Red blink notification for new messages */}
                  {hasNewMessage && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="flex items-center space-x-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium animate-pulse shadow-lg">
                        <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                        <span>New Message</span>
                      </div>
                      {messageNotification && (
                        <div className="absolute top-8 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-64 z-20">
                          <div className="text-xs text-gray-600 mb-1">
                            From: {messageNotification.senderName}
                          </div>
                          <div className="text-sm text-gray-900 truncate">
                            {messageNotification.message}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigateToTicket(ticket.id)}
                    >
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-blue-600">
                          {ticket.custom_id || ticket.display_id || `#${ticket.id}`}
                        </span>
                        {getStatusBadge(ticket.status)}
                        {getPriorityBadge(ticket.priority)}
                        {slaIndicator}
                        {needsAttention && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Needs Attention
                          </span>
                        )}
                        {/* Show assignment status for staff */}
                        {userAccessLevel === 'assigned_only' && ticket.agent_id === user?.id && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <User className="h-3 w-3 mr-1" />
                            Assigned to Me
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-medium text-gray-900 truncate">
                        {ticket.title}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                        {ticket.description}
                      </p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Created {formatDate(ticket.created_at)}
                        </span>
                        {ticket.agent && userAccessLevel !== 'assigned_only' && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {ticket.agent.name || ticket.agent.email}
                          </span>
                        )}
                        {ticket.category && (
                          <span className="flex items-center gap-1">
                            <Filter className="h-3 w-3" />
                            {ticket.category.name}
                          </span>
                        )}
                        {resolutionTime && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Resolved in {resolutionTime}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      {chatAvailable && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenChat(ticket);
                          }}
                          className={`p-2 rounded-lg transition-colors relative ${
                            hasNewMessage 
                              ? 'text-red-600 hover:text-red-800 hover:bg-red-50 animate-pulse' 
                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                          }`}
                          title="Open chat with assigned agent"
                        >
                          <MessageCircle className="h-4 w-4" />
                          {hasNewMessage && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                          )}
                        </button>
                      )}
                      
                      <button
                        onClick={() => navigateToTicket(ticket.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                        title="View ticket details"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.last_page > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow-sm">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handleFilterChange('page', Math.max(1, pagination.current_page - 1))}
              disabled={pagination.current_page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handleFilterChange('page', Math.min(pagination.last_page, pagination.current_page + 1))}
              disabled={pagination.current_page >= pagination.last_page}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{pagination.from}</span>
                {' '}to{' '}
                <span className="font-medium">{pagination.to}</span>
                {' '}of{' '}
                <span className="font-medium">{pagination.total}</span>
                {' '}results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handleFilterChange('page', Math.max(1, pagination.current_page - 1))}
                  disabled={pagination.current_page <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronRight className="h-5 w-5 rotate-180" aria-hidden="true" />
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  const pageNum = i + 1;
                  const isCurrent = pageNum === pagination.current_page;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handleFilterChange('page', pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        isCurrent
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handleFilterChange('page', Math.min(pagination.last_page, pagination.current_page + 1))}
                  disabled={pagination.current_page >= pagination.last_page}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketList;